import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

type Args = {
  bankAccountIds: string[];
  asOfDate: string; // YYYY-MM-DD
  refreshKey?: number;
};

export type ClosingAsOfRow = {
  bank_account_id: string;
  closing_balance: number | null;
  currency: string | null;
  source: "manual" | "transaction" | null;
  balance_day: string | null;
  balance_ts: string | null;
};

const toNum = (v: unknown): number | null => {
  if (v == null) return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * Latest closing balance per account as of a date.
 *
 * Manual closing balances always count — including for accounts that have
 * no imported bank statements at all.
 */
export function useClosingAsOf(args: Args) {
  const { bankAccountIds, asOfDate, refreshKey = 0 } = args;

  return useQuery({
    queryKey: ["bank_closing_as_of", [...bankAccountIds].sort(), asOfDate, refreshKey],
    enabled: bankAccountIds.length > 0 && !!asOfDate,
    queryFn: async (): Promise<Map<string, ClosingAsOfRow>> => {
      const { data, error } = await (supabase as any).rpc("get_bank_closing_as_of", {
        p_account_ids: bankAccountIds,
        p_as_of: asOfDate,
      });

      if (error) throw error;

      const map = new Map<string, ClosingAsOfRow>();
      for (const row of (data ?? []) as any[]) {
        map.set(String(row.bank_account_id), {
          bank_account_id: String(row.bank_account_id),
          closing_balance: toNum(row.closing_balance),
          currency: row.currency ?? null,
          source: (row.source ?? null) as ClosingAsOfRow["source"],
          balance_day: row.balance_day ? String(row.balance_day).slice(0, 10) : null,
          balance_ts: row.balance_ts ?? null,
        });
      }
      return map;
    },
    staleTime: 15_000,
  });
}
